'use client';

import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import type { ColumnDef } from '@tanstack/react-table';
import { MathLib } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt } from '@morpho-org/dapp-core';
import { HoverableCell, MarketCell } from '@morpho-org/ui-v6/client';
import { CellSimpleText } from '@morpho-org/ui-v6/server';
import { formatters, getMarketName } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { RewardsGradient } from '@repo/ui/icons';
import { createPaginatedRowModel, createSortedRowModel, rowPaginationFeature, rowSortingFeature, sortFns, tableFeatures } from '@tanstack/react-table';
import { useMemo } from 'react';
import MerklRewardsHoverCard from '@/components/common/RewardsHoverCard/MerklRewardsHoverCard';
import { WarningIcon } from '@/components/common/WarningIcon';
import { APY_DURATION_LABEL } from '@/constants/avgApy';
import { getParsedValue } from '@/helpers/formatters';
import { sortingStringFn } from '@/helpers/sorting';
import type { MarketAllocationsTableRow } from './MarketAllocation.types';
import VaultPendingTimelocks from '../components/VaultPendingTimelocks';
import CellAllocationWithAmount from './cells/CellAllocationWithAmount';
export const marketAllocationTableFeatures = tableFeatures({
  rowPaginationFeature,
  rowSortingFeature,
  paginatedRowModel: createPaginatedRowModel(),
  sortFns,
  sortedRowModel: createSortedRowModel()
});
export type MarketAllocationTableFeatures = typeof marketAllocationTableFeatures;
export const getColumnsMarketAllocation = ({
  vaultAddress,
  chainId,
  vaultAsset
}: {
  vaultAddress: Address;
  chainId: ChainId;
  vaultAsset: InterfaceToken | null | undefined;
}): ColumnDef<MarketAllocationTableFeatures, MarketAllocationsTableRow>[] => [{
  accessorKey: 'market',
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Market Exposure</span>
        </TooltipTrigger>
        <TooltipContent>The markets this vault has supplied into.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    const market = row.original.market;
    const pendingTimelocks = row.original.pendingTimelocks;
    return <MarketCell collateralAsset={market.collateralAsset} loanAsset={market.loanAsset} lltv={market.lltv} chainId={market.loanAsset.chain.id} renderWarnings={<WarningIcon warnings={market.warnings} />} renderPendingTimelocks={pendingTimelocks.length > 0 ? <VaultPendingTimelocks chainId={chainId} pendingTimelocks={pendingTimelocks} currentSupplyCap={row.original.supplyCap} loanAsset={market.loanAsset} collateralAsset={market.collateralAsset} /> : undefined} />;
  },
  sortFn: (a, b) => sortingStringFn(getMarketName({
    loanAsset: a.original.market.loanAsset,
    collateralAsset: a.original.market.collateralAsset
  }), getMarketName({
    loanAsset: b.original.market.loanAsset,
    collateralAsset: b.original.market.collateralAsset
  }))
}, {
  accessorKey: 'supplyAssetsUsd',
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {vaultAsset ? `Vault Allocation (${vaultAsset.symbol})` : 'Vault Allocation'}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The amount and proportion of the vault supply allocated to this Morpho Market.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <CellAllocationWithAmount allocation={row.original.percentShare} supplyAssets={toBigInt(row.original.supplyAssets) ?? 0n} token={vaultAsset} percentageOffset={row.original.percentageOffset} index={row.index} />
}, {
  id: 'liquidity',
  accessorFn: row => row.liquidity,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {vaultAsset ? `Liquidity (${vaultAsset.symbol})` : 'Liquidity'}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The amount of liquidity available for withdrawal from this market.
        </TooltipContent>
      </Tooltip>,
  cell: function LiquidityCell({
    row
  }) {
    const loanAsset = row.original.market.loanAsset;
    const asset = useMemo(() => getParsedValue(loanAsset, parseApiAsset), [loanAsset]);
    const formatted = useMemo(() => {
      return formatters.short.default('-').trailingZero(false).digits(2).of(row.original.liquidity, asset?.decimals);
    }, [row.original.liquidity, asset]);
    const formattedUsd = useMemo(() => {
      const usdValue = asset?.toUsd(row.original.liquidity) ?? 0n;
      return formatters.short.default('0').trailingZero(false).digits(2).unit('$').of(usdValue, 18);
    }, [row.original.liquidity, asset]);
    return <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-2xs text-foreground">{formatted}</span>
          </TooltipTrigger>
          <TooltipContent>{formattedUsd}</TooltipContent>
        </Tooltip>;
  }
}, {
  id: 'underlyingLiquidity',
  accessorFn: row => row.underlyingLiquidity,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {vaultAsset ? `Underlying Liquidity (${vaultAsset.symbol})` : 'Underlying Liquidity'}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The underlying liquidity of this market (not including public allocator liquidity).
        </TooltipContent>
      </Tooltip>,
  cell: function LiquidityCell({
    row
  }) {
    const isIdle = !row.original.market.collateralAsset;
    const loanAsset = row.original.market.loanAsset;
    const asset = useMemo(() => getParsedValue(loanAsset, parseApiAsset), [loanAsset]);
    const formatted = useMemo(() => {
      return formatters.short.default('-').trailingZero(false).digits(2).of(row.original.underlyingLiquidity, asset?.decimals);
    }, [row.original.underlyingLiquidity, asset]);
    const formattedUsd = useMemo(() => {
      const usdValue = asset?.toUsd(row.original.underlyingLiquidity) ?? 0n;
      return formatters.short.default('0').trailingZero(false).digits(2).unit('$').of(usdValue, 18);
    }, [row.original.underlyingLiquidity, asset]);
    if (isIdle) return '-';
    return <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-2xs text-foreground">{formatted}</span>
          </TooltipTrigger>
          <TooltipContent>{formattedUsd}</TooltipContent>
        </Tooltip>;
  }
}, {
  accessorKey: 'supplyCapUsd',
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Supply Cap</span>
        </TooltipTrigger>
        <TooltipContent>The maximum market allocation allowed by the vault.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    if (!row.original.market.collateralAsset) {
      return '-';
    }
    const supplyCap = toBigInt(row.original.supplyCap) ?? 0n;
    if (supplyCap >= MathLib.RAY) {
      return <CellSimpleText>Infinite</CellSimpleText>;
    }
    return <CellSimpleText>
          {formatters.short.trailingZero(false).digits(2).unit('$').of(row.original.supplyCapUsd)}
        </CellSimpleText>;
  }
}, {
  accessorKey: 'market.state.netSupplyApy',
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '14px'
      }}>
            APY
          </span>
        </TooltipTrigger>
        <TooltipContent>{`${APY_DURATION_LABEL} Supply APY + Rewards`}</TooltipContent>
      </Tooltip>,
  cell: function ApyCell({
    row
  }) {
    const apy = useMemo(() => {
      return getParsedValue(row.original.market.state?.supplyApy, safeParseNumber);
    }, [row.original.market.state?.supplyApy]);
    const netApy = useMemo(() => {
      return getParsedValue(row.original.market.state?.netSupplyApy, safeParseNumber);
    }, [row.original.market.state?.netSupplyApy]);
    if (!row.original.market.collateralAsset) {
      return <CellSimpleText style={{
        marginLeft: '14px'
      }}>-</CellSimpleText>;
    }
    return <HoverableCell hoverCardContent={<MerklRewardsHoverCard apy={apy} netApy={netApy} rewards={row.original.rewards} variant="supply" asset={row.original.market.collateralAsset} merklContext={{
      kind: 'vault',
      chainId,
      identifier: vaultAddress
    }} />} padding="0 11px 0 14px">
          <div className="flex items-center gap-[2px]">
            <CellSimpleText>{formatters.percent.digits(2).unit('%').of(netApy, 18)}</CellSimpleText>
            {row.original.rewards.length > 0 ? <Icon icon={<RewardsGradient />} /> : null}
          </div>
        </HoverableCell>;
  }
}, {
  id: 'utilization',
  accessorFn: row => row.market.state?.utilization,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Utilization</span>
        </TooltipTrigger>
        <TooltipContent>Share of supplied liquidity that’s currently borrowed.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    if (!row.original.market.collateralAsset) {
      return '-';
    }
    return <span className="text-foreground text-xs">
          {formatters.percent.digits(2).unit('%').of(row.original.market.state?.utilization)}
        </span>;
  }
}];
export const columnLabelsMarketAllocation = {
  market: 'Market',
  supplyAssetsUsd: 'Allocation',
  supplyCapUsd: 'Cap',
  market_state_dailyNetSupplyApy: 'Daily Supply APY'
};